import { ENV_VARS } from 'ENV_CONFIG';

import { store } from '~/store';

import { handleResponse, handleJSONResponse, handleFetchError, handleApiErrors } from './helpers';
import { getAccessToken } from '~/hocs/utils';
import { addAbortableRequest, removeAbortableRequest } from './api.actions';
import { customApiErrors } from './api.custom.errors';

const CONFIG_NAMES = {
  SERVICE_ID: Symbol('SERVICE_ID'),
  BASENAME: Symbol('BASENAME'),
};

const apiPath = (host) => `${host}/appointments/bff/v1/`;
export const bffPath = () => apiPath(ENV_VARS.REACT_APP_API_HOST);

const signInPath = () => ENV_VARS.REACT_APP_SIGN_IN_HOST;

const continueToPath = () => encodeURIComponent(window.location.href);

const apiFetch = async (url, options = {}, bypassErrorCodes = [], isRejectIgnored = false) => {
  const fetchOptions = {
    credentials: 'include',
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: {
      Accept: 'application/json; charset=UTF-8',
      'Content-Type': 'application/json; charset=UTF-8',
      appId: ENV_VARS.APP_ID,
      Pragma: 'no-cache',
      ...options.headers,
    },
  };
  const accessToken = await getAccessToken();
  if (accessToken) {
    fetchOptions.headers = {
      ...fetchOptions.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }
  return fetch(`${bffPath()}${url}`, fetchOptions)
    .then(
      handleResponse,
      isRejectIgnored ? undefined : () => handleFetchError(`${bffPath()}${url}`),
    )
    .catch(handleApiErrors(options.method, bypassErrorCodes));
};

const apiFetchJSON = (...params) => apiFetch(...params).then(handleJSONResponse);

export const fetchJSONFromAPI = async (url, options = {}, bypassErrorCodes = []) => {
  try {
    const abortController = new AbortController();

    store.dispatch(addAbortableRequest({ url, controller: abortController }));

    const response = await apiFetch(
      url,
      {
        ...options,
        signal: abortController.signal,
      },
      bypassErrorCodes,
      true,
    );

    store.dispatch(removeAbortableRequest({ url }));

    return Promise.resolve(response.json());
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new customApiErrors.AbortableRequestError(err.message);
    }
    return handleFetchError(`${bffPath()}${url}`);
  }
};

const apiFetchSource = (...params) =>
  fetch(...params)
    .then((response) => {
      if (response.status === 200) {
        return response;
      }

      return Promise.reject(response);
    })
    .catch((reason) => Promise.reject(reason));

export const api = {
  fetch: apiFetch,
  fetchJSON: apiFetchJSON,
  fetchSource: apiFetchSource,
  fetchJSONFromAPI,
};

api.getSignInUrl = () => `${signInPath()}/sign-in?continueTo=${continueToPath()}`;

Object.defineProperty(api, 'SERVICE_ID', {
  get() {
    return api[CONFIG_NAMES.SERVICE_ID];
  },
  set(SERVICE_ID) {
    api[CONFIG_NAMES.SERVICE_ID] = SERVICE_ID;
  },
});

Object.defineProperty(api, 'BASENAME', {
  get() {
    return api[CONFIG_NAMES.BASENAME];
  },
  set(BASENAME) {
    api[CONFIG_NAMES.BASENAME] = BASENAME;
  },
});
